import React, { useState, useEffect } from 'react';
import { 
  History, 
  Plus, 
  Edit3, 
  Trash2,
  User,
  AlertCircle,
  Clock,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { pacienteMedicamentoService } from '../services';

const HistoricoMedicamentos = ({ pacienteId }) => {
  const [historico, setHistorico] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandido, setExpandido] = useState({});
  const [error, setError] = useState('');

  useEffect(() => {
    carregarHistorico();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pacienteId]);

  const carregarHistorico = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await pacienteMedicamentoService.getHistorico(pacienteId);
      setHistorico(response);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      setError('Erro ao carregar histórico de modificações');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpansao = (index) => {
    setExpandido(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const getIconeAcao = (tipo) => {
    switch (tipo) {
      case 'criacao':
        return <Plus size={16} color="#28a745" />;
      case 'edicao':
        return <Edit3 size={16} color="#ffc107" />;
      case 'exclusao':
        return <Trash2 size={16} color="#dc3545" />;
      default:
        return <AlertCircle size={16} color="#6c757d" />;
    }
  };

  const getCorAcao = (tipo) => {
    switch (tipo) {
      case 'criacao':
        return '#d4edda';
      case 'edicao':
        return '#fff3cd';
      case 'exclusao':
        return '#f8d7da';
      default:
        return '#f8f9fa';
    }
  };

  const formatarValor = (campo, valor) => {
    if (valor === null || valor === undefined) {
      return '-';
    }

    switch (campo) {
      case 'quantidade_mensal':
        return `${valor} unidades`;
      case 'tipo_retirada':
        return valor === 'semanal' ? 'Semanal' : 'Mensal';
      case 'frequencia_semanas':
        return valor ? `A cada ${valor} semana${valor > 1 ? 's' : ''}` : '-';
      case 'dia_retirada':
        return valor ? `Dia ${valor}` : '-';
      case 'intervalo_meses':
        return valor ? (valor === 1 ? 'Mensal' : `A cada ${valor} meses`) : '-';
      case 'ativo':
        return valor ? 'Ativo' : 'Inativo';
      default:
        return valor.toString();
    }
  };

  const getNomeCampo = (campo) => {
    const nomes = {
      quantidade_mensal: 'Quantidade Mensal',
      dosagem_prescrita: 'Dosagem Prescrita',
      observacoes: 'Observações',
      tipo_retirada: 'Tipo de Retirada',
      frequencia_semanas: 'Frequência (semanas)',
      dia_retirada: 'Dia da Retirada',
      intervalo_meses: 'Intervalo (meses)',
      ativo: 'Status'
    };
    return nomes[campo] || campo;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div className="spinner"></div>
        <p>Carregando histórico...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '40px',
        backgroundColor: '#f8d7da',
        color: '#721c24',
        borderRadius: '8px',
        border: '1px solid #f5c6cb'
      }}>
        <AlertCircle size={48} style={{ marginBottom: '15px' }} />
        <h4>{error}</h4>
      </div>
    );
  }

  if (historico.length === 0) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '40px', 
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e9ecef'
      }}>
        <History size={48} color="#6c757d" style={{ marginBottom: '15px' }} />
        <h4 style={{ color: '#6c757d', marginBottom: '10px' }}>Nenhum histórico encontrado</h4>
        <p style={{ color: '#6c757d' }}>
          Não há registros de modificações para este paciente.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: '20px',
        gap: '8px'
      }}>
        <span style={{ 
          backgroundColor: '#007bff',
          color: 'white',
          fontSize: '12px',
          padding: '2px 8px',
          borderRadius: '12px'
        }}>
          {historico.length} registro{historico.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {historico.map((registro, index) => (
          <div 
            key={index}
            style={{
              backgroundColor: getCorAcao(registro.tipo),
              border: `1px solid ${registro.tipo === 'criacao' ? '#c3e6cb' : 
                                    registro.tipo === 'edicao' ? '#ffeaa7' : '#f5c6cb'}`,
              borderRadius: '8px',
              padding: '16px'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  {getIconeAcao(registro.tipo)}
                  <h4 style={{ margin: 0, color: '#333', fontSize: '16px' }}>
                    {registro.titulo}
                  </h4>
                </div>
                
                <p style={{ margin: '0 0 8px 0', color: '#667', fontSize: '14px' }}>
                  {registro.descricao}
                </p>
                
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', fontSize: '13px', color: '#6c757d' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <User size={14} />
                    {registro.usuario}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Clock size={14} />
                    {registro.data}
                  </span>
                </div>
              </div>
              
              {(registro.dados || registro.diferencas) && (
                <button
                  onClick={() => toggleExpansao(index)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#007bff',
                    padding: '4px'
                  }}
                  title={expandido[index] ? 'Ocultar detalhes' : 'Ver detalhes'}
                >
                  {expandido[index] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
              )}
            </div>

            {/* Detalhes expandidos */}
            {expandido[index] && (
              <div style={{ 
                marginTop: '16px', 
                paddingTop: '16px', 
                borderTop: '1px solid #e9ecef' 
              }}>
                {registro.tipo === 'criacao' && registro.dados && (
                  <div>
                    <h5 style={{ margin: '0 0 8px 0', color: '#333' }}>Dados da vinculação:</h5>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px' }}>
                      {Object.entries(registro.dados).map(([campo, valor]) => (
                        <div key={campo} style={{ fontSize: '13px' }}>
                          <strong>{getNomeCampo(campo)}:</strong> {formatarValor(campo, valor)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {registro.tipo === 'edicao' && registro.diferencas && (
                  <div>
                    <h5 style={{ margin: '0 0 8px 0', color: '#333' }}>Campos alterados:</h5>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {Object.entries(registro.diferencas).map(([campo, mudanca]) => (
                        <div key={campo} style={{ 
                          backgroundColor: 'rgba(255,255,255,0.7)', 
                          padding: '8px', 
                          borderRadius: '4px',
                          fontSize: '13px'
                        }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                            {getNomeCampo(campo)}:
                          </div>
                          <div style={{ display: 'flex', gap: '16px' }}>
                            <span style={{ color: '#dc3545' }}>
                              <strong>Anterior:</strong> {formatarValor(campo, mudanca.anterior)}
                            </span>
                            <span style={{ color: '#28a745' }}>
                              <strong>Novo:</strong> {formatarValor(campo, mudanca.novo)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {registro.tipo === 'exclusao' && registro.dados && (
                  <div>
                    <h5 style={{ margin: '0 0 8px 0', color: '#333' }}>Dados removidos:</h5>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '8px' }}>
                      {Object.entries(registro.dados).map(([campo, valor]) => (
                        <div key={campo} style={{ fontSize: '13px' }}>
                          <strong>{getNomeCampo(campo)}:</strong> {formatarValor(campo, valor)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default HistoricoMedicamentos;